// Libraries
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Components
import Container from "@shared/components/Container/Container";
import Title, {
  TitleTag,
  TitleVariant,
} from "@shared/components/typography/Title";
import Modal from "@shared/components/Modal/Modal";
// assets
import { ReactComponent as Logo } from "@shared/assets/icons/logo.svg";
import { ReactComponent as Logout } from "@shared/assets/icons/logout.svg";
import { Tag } from "@shared/common/types/tags";
import { useMsal } from "@azure/msal-react";
import AppFeedback from "@shared/components/AppFeedback/AppFeedback";
import Select from "@shared/components/Select/Select";
import { Text } from "@shared/components/typography";
import styles from "@components/Layout/Layout.module.scss";
import { useGetCurrentUserOrganisationsQuery, useGetCurrentUserQuery } from "@api/users";
import { selectUserId } from "@selectors/authSelector";
import { FormField } from "@shared/common/types/main";
import { selectOrganisationContextId } from "@selectors/appSelector";
import { setOrganisationContextId } from "@store/slices/appSlice";
import { useSendAppFeedbackMutation, useUploadFeedbackFileMutation } from "@api/chats";
import { AppFeedbackRequestData } from "@shared/models/feedback";

const TopBar = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();

  const userId = useSelector(selectUserId);
  const organisationContextId = useSelector(selectOrganisationContextId);

  const { data: user } = useGetCurrentUserQuery(
    { organisationId: organisationContextId! },
    { skip: !userId || !organisationContextId },
  );
  const { data: organisationDiscovery } = useGetCurrentUserOrganisationsQuery(undefined, {
    skip: !userId,
  });
  const [uploadFeedbackFileTrigger] = useUploadFeedbackFileMutation();
  const [sendAppFeedbackTrigger] = useSendAppFeedbackMutation();

  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const isLogged = !!user;

  const logout = async () => {
    await instance.logoutRedirect();
  };

  const organisations = useMemo(
    () => organisationDiscovery?.organisations.map(x => ({ value: x.id, label: x.name })) ?? []
    , [organisationDiscovery]
  );

  useEffect(() => {
    if (!organisationContextId && organisations.length > 0) {
      dispatch(setOrganisationContextId(organisations[0].value));
    }
  }, [dispatch, organisationContextId, organisations]);

  const currentOrganisation = useMemo(() => {
    if (organisations.length === 0) return null;
    if (!organisationContextId) {
      return organisations[0];
    }
    return organisations.find(x => x.value === organisationContextId) ?? organisations[0];
  }, [organisations, organisationContextId]);

  const uploadFileFeedback = useCallback(
    async (file: File, _token: string) => {
      return await uploadFeedbackFileTrigger({ file }).unwrap();
    },
    [uploadFeedbackFileTrigger]
  );

  const appFeedbackRequest = useCallback(
    async (data: AppFeedbackRequestData, _token: string) => {
      const newData = { ...data, organisation_id: organisationContextId };
      return await sendAppFeedbackTrigger(newData).unwrap();
    },
    [sendAppFeedbackTrigger, organisationContextId]
  );

  if (!organisations || !currentOrganisation) {
    return null;
  }

  return (
    <>
      <Container as={Tag.Nav} classes="top-bar">
        <Title tag={TitleTag.h1} variant={TitleVariant.main}>
          <Link to="/">
            <Logo />
          </Link>
        </Title>
        <div className={styles.wrapperSelect}>
          <Text>Organisation context:</Text>
          <Select<string>
            id="org_context"
            name="org_context"
            label=""
            value={currentOrganisation.value}
            valueChangeHandler={(name: string, value: FormField) => dispatch(setOrganisationContextId(value.value))}
            defaultOption={currentOrganisation}
            classes={styles.select}
            options={organisations}
            required={true}
          />
        </div>
        {isLogged && (
          <div className="top-bar__profile">
            <button
              className="top-bar__link"
              onClick={() => {
                setIsOpenFeedback(true);
              }}
            >
              share feedback
            </button>
            <div className="top-bar__account">
              <div className="top-bar__profile-picture">
                {user.display_name?.slice(0, 1)}
              </div>
              <p className="top-bar__profile-nick">{user?.display_name}</p>
              <div className="top-bar__account-options">
                <button onClick={logout} className="top-bar__account-select">
                  <Logout />
                  Log out
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>
      {isOpenFeedback && (
        <Modal
          closeHandler={() => {
            setIsOpenFeedback(false);
          }}
          wrapperClasses="feedback__wrapper"
        >
          <AppFeedback
            closeHandler={() => {
              setIsOpenFeedback(false);
            }}
            appFeedbackRequest={appFeedbackRequest}
            feedbackUploadFileRequest={uploadFileFeedback}
            userToken={`${userId}`}
          />
        </Modal>
      )}
    </>
  );
};

export default TopBar;
